// クリエイター追加モーダル。常に status=candidate（営業リスト＝段階0）で作成する。案件への追加は AddEntryModal。
// 「Instagramから選ぶ」（第1.5弾）= ig-harness の相手を選んで import-ig で登録＋紐付け（＋任意で案件に追加）をサーバー側1本で行う。
import { useEffect, useState } from "react";
import type { GfCreateInfluencerInput, GfCreatorFormModalProps, GfIgFriend, GfPlatform, GfSnsInput } from "../../../types/gifting";
import { createGfInfluencer, importGfIg } from "../../../api/giftingClient";
import { PLATFORM_LABEL, STAGE_NAMES, campaignAddBlocker } from "../../../lib/giftingRules";
import { GfButton, GfErrorBanner, GfField, GfModal, GfSeg } from "../ui/GfUi";
import { IgPicker } from "../ui/IgPicker";
import { errMsg, parseCount, stripAt } from "../tabs/kit/helpers";

interface SnsRow {
  key: number;
  platform: GfPlatform;
  handle: string;
  followers: string;
}

const PLATFORM_OPTIONS: GfPlatform[] = ["instagram", "youtube", "tiktok", "ameblo", "x", "threads", "other"];
const CHANNEL_LABEL = { ig_dm: "Instagram DM", email: "メール", other: "その他" } as const;
const SOURCE_LABEL = { outbound: "こちらから打診（outbound）", inbound: "先方から連絡（inbound）" } as const;

let rowSeq = 0;
const newRow = (platform: GfPlatform = "instagram"): SnsRow => ({ key: ++rowSeq, platform, handle: "", followers: "" });

type Mode = "ig" | "manual";
const IMPORT_STAGES = [1, 2, 3] as const;

export function CreatorFormModal({ open, onClose, onSaved, refresh, toast, data, initialHandle }: GfCreatorFormModalProps) {
  const [mode, setMode] = useState<Mode>("ig");
  const [igPick, setIgPick] = useState<GfIgFriend | null>(null);
  const [igCampaign, setIgCampaign] = useState("");
  const [igStage, setIgStage] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState("");
  const [genre, setGenre] = useState("");
  const [source, setSource] = useState<"inbound" | "outbound">("outbound");
  const [channel, setChannel] = useState<"ig_dm" | "email" | "other">("ig_dm");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState<SnsRow[]>([newRow()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 開くたびに初期化
  useEffect(() => {
    if (!open) return;
    // 検知した投稿から「クリエイターとして追加」するとき: ハンドル入りの手動入力で開く（先方からのタグ付け＝inbound）
    const handle = initialHandle ? stripAt(initialHandle) : "";
    setMode(handle ? "manual" : "ig");
    setIgPick(null);
    setIgCampaign("");
    setIgStage(1);
    setName(handle);
    setGenre("");
    setSource(handle ? "inbound" : "outbound");
    setChannel("ig_dm");
    setEmail("");
    setNotes("");
    setRows([handle ? { ...newRow("instagram"), handle } : newRow()]);
    setBusy(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patchRow = (key: number, patch: Partial<SnsRow>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const activeCampaigns = data.board.campaigns.filter((c) => c.status === "active");
  const igLinked = new Set<number>();
  for (const cr of data.board.creators) for (const s of cr.sns) if (s.ig_follower_id != null) igLinked.add(s.ig_follower_id);

  const submitIg = async () => {
    if (!igPick) return setError("Instagramの相手を選んでください（または「手動で入力」へ）");
    // 送る商品が未設定の案件だとサーバーでエントリー作成だけ失敗する（クリエイターは登録される）ので、先に止める
    const blocker = igCampaign ? campaignAddBlocker(activeCampaigns.find((c) => c.id === igCampaign)) : null;
    if (blocker) return setError(blocker);
    setBusy(true);
    setError(null);
    try {
      const res = await importGfIg({
        ig_follower_id: igPick.id,
        genre: genre.trim() || null,
        ...(igCampaign ? { campaign_id: igCampaign, stage: igStage } : {}),
      });
      const who = `@${igPick.username ?? igPick.id}`;
      const added = res.deal_id ? "（案件に追加しました）" : "";
      toast(res.existing ? `${who} は登録済みのクリエイターです${added}` : `${who} を登録しました${added}`);
      await refresh();
      onSaved(res.influencer_id, res.deal_id);
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  const submit = async () => {
    if (busy) return;
    if (mode === "ig") return submitIg();
    const n = name.trim();
    if (!n) return setError("名前を入力してください");
    const em = email.trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return setError("メールアドレスの形式が正しくありません");
    const sns: GfSnsInput[] = [];
    for (const r of rows) {
      const h = stripAt(r.handle);
      if (!h) continue;
      const f = parseCount(r.followers);
      if (f !== null && Number.isNaN(f)) return setError(`${PLATFORM_LABEL[r.platform]} のフォロワー数は数字（例: 12400 / 1.2万）で入力してください`);
      sns.push({ platform: r.platform, handle: h, follower_count: f, is_primary: sns.length === 0 ? 1 : 0 });
    }
    const input: GfCreateInfluencerInput = {
      name: n,
      genre: genre.trim() || null,
      status: "candidate",
      source,
      contact_channel: channel,
      email: em || null,
      notes: notes.trim() || null,
      sns,
    };
    setBusy(true);
    setError(null);
    try {
      const res = await createGfInfluencer(input);
      toast("クリエイターを追加しました");
      await refresh();
      onSaved(res.id);
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <GfModal
      open={open}
      title="クリエイターを追加"
      width={560}
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <GfButton onClick={onClose} disabled={busy}>
            キャンセル
          </GfButton>
          <GfButton variant="primary" onClick={() => void submit()} disabled={busy || (mode === "ig" && !igPick)}>
            {busy ? "追加中…" : mode === "ig" ? "この人を登録する" : "追加する"}
          </GfButton>
        </>
      }
    >
      <div className="gf-modal-form">
        <GfSeg<Mode>
          ariaLabel="追加のしかた"
          options={[
            { value: "ig", label: "Instagramから選ぶ" },
            { value: "manual", label: "手動で入力" },
          ]}
          value={mode}
          onChange={(m) => {
            setMode(m);
            setError(null);
          }}
        />
        {error && <GfErrorBanner message={error} />}
        {mode === "ig" ? (
          <>
            {igPick ? (
              <div className="gf-boxed gf-ig-picked">
                <span>
                  選択中: <span className="gf-mono">@{igPick.username || "—"}</span>
                  {igPick.display_name ? `（${igPick.display_name}）` : ""}
                  {igLinked.has(igPick.id) && <span className="gf-ig-linked">登録済み</span>}
                </span>
                <button type="button" className="gf-link-btn" onClick={() => setIgPick(null)} disabled={busy}>
                  選び直す
                </button>
              </div>
            ) : (
              <IgPicker pickLabel="選ぶ" disabled={busy} linkedIds={igLinked} onPick={(f) => setIgPick(f)} />
            )}
            <GfField label="ジャンル">
              <input className="gf-input" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="例）暮らし・料理" maxLength={50} />
            </GfField>
            <div className="gf-form-grid">
              <GfField label="案件に追加（任意）">
                <select className="gf-select" value={igCampaign} onChange={(e) => setIgCampaign(e.target.value)}>
                  <option value="">追加しない（営業リストへ）</option>
                  {activeCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </GfField>
              {igCampaign && (
                <GfField label="開始する段階">
                  <select className="gf-select" value={igStage} onChange={(e) => setIgStage(Number(e.target.value) as 1 | 2 | 3)}>
                    {IMPORT_STAGES.map((s) => (
                      <option key={s} value={s}>
                        {s} {STAGE_NAMES[s]}
                      </option>
                    ))}
                  </select>
                </GfField>
              )}
            </div>
            <div className="gf-note">
              名前・ハンドルは Instagram のものを使い、DM連携まで済ませて登録します（起点=先方から・連絡手段=Instagram DM）。登録済みの相手を選んだときは、既存のクリエイターを使います。
            </div>
          </>
        ) : (
          <>
        <div className="gf-form-grid">
          <GfField label="名前（必須）">
            <input className="gf-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
          </GfField>
          <GfField label="ジャンル">
            <input className="gf-input" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="例）暮らし・料理" maxLength={50} />
          </GfField>
          <GfField label="起点">
            <select className="gf-select" value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
              {(Object.keys(SOURCE_LABEL) as Array<keyof typeof SOURCE_LABEL>).map((k) => (
                <option key={k} value={k}>
                  {SOURCE_LABEL[k]}
                </option>
              ))}
            </select>
          </GfField>
          <GfField label="連絡手段">
            <select className="gf-select" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
              {(Object.keys(CHANNEL_LABEL) as Array<keyof typeof CHANNEL_LABEL>).map((k) => (
                <option key={k} value={k}>
                  {CHANNEL_LABEL[k]}
                </option>
              ))}
            </select>
          </GfField>
        </div>
        <GfField label="メール">
          <input className="gf-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="任意" maxLength={200} />
        </GfField>

        <div className="gf-panel-section">
          <div className="gf-panel-section-title">投稿媒体（SNS）</div>
          {rows.map((r) => (
            <div className="gf-sns-edit-row" key={r.key}>
              <select
                className="gf-select"
                aria-label="媒体"
                value={r.platform}
                onChange={(e) => patchRow(r.key, { platform: e.target.value as GfPlatform })}
              >
                {PLATFORM_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {PLATFORM_LABEL[p]}
                  </option>
                ))}
              </select>
              <input
                className="gf-input"
                aria-label="ハンドル"
                placeholder="ハンドル（@なし）"
                value={r.handle}
                onChange={(e) => patchRow(r.key, { handle: e.target.value })}
              />
              <input
                className="gf-input is-mono"
                aria-label="フォロワー数"
                placeholder="フォロワー数"
                inputMode="decimal"
                value={r.followers}
                onChange={(e) => patchRow(r.key, { followers: e.target.value })}
              />
              <button
                type="button"
                className="gf-icon-btn"
                aria-label="この行を削除"
                disabled={rows.length === 1}
                onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
              >
                ×
              </button>
            </div>
          ))}
          <div>
            <GfButton size="sm" onClick={() => setRows((rs) => [...rs, newRow("youtube")])}>
              ＋ 媒体を追加
            </GfButton>
          </div>
        </div>

        <GfField label="メモ">
          <textarea className="gf-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />
        </GfField>
        <div className="gf-note">追加すると営業リスト（未打診）に入ります。案件への追加は営業タブから行います。</div>
          </>
        )}
      </div>
    </GfModal>
  );
}
